import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, Optional, Self } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

@Component({
  selector: 'app-checkbox',
  standalone: true, // Assuming Angular 17+ based on @if syntax
  imports: [CommonModule],
  template: `
  <label
    [for]="id"
    class="flex items-center space-x-3 group cursor-pointer"
    [ngClass]="{ 'cursor-not-allowed opacity-60': disabled }">
    
    <div class="relative w-5 h-5">
      <input
        [id]="id"
        type="checkbox"
        class="w-5 h-5 appearance-none cursor-pointer border border-text checked:border-primary  checked:bg-primary disabled:opacity-60"
        [ngClass]="className"
        [checked]="checked"
        (change)="onInput($event)"
        [disabled]="disabled"
      />
      
      @if (checked) {
        <svg
          class="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-none top-1/2 left-1/2"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
            stroke="white"
            stroke-width="1.94437"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      }

      @if (disabled) {
        <svg
          class="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-none top-1/2 left-1/2"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
            stroke="#E4E7EC"
            stroke-width="2.33333"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      }
    </div>

    @if (label) {
      <span
        class="text-sm font-medium text-text"
        [ngClass]="labelClass">
          {{ label }}
      </span>
    }
  </label>
  `,
  styles: ``
})
export class CheckboxComponent implements ControlValueAccessor {

  @Input() label?: string;
  @Input() checked = false;
  // 🔹 Added a default unique ID to ensure labeling always works
  @Input() id: string = 'checkbox-' + Math.random().toString(36).substring(2, 9);
  @Input() disabled = false;
  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() valueChange = new EventEmitter<string | number>();
  @Input() className?: string;
  @Input() labelClass?: string;

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  get control() {
    return this.ngControl?.control;
  }

  get invalid(): boolean {
    return !!this.control?.invalid && (this.control?.touched || this.control?.dirty);
  }

  writeValue(value: any): void {
    this.checked = !!value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.checked = input.checked;
    this.checkedChange.emit(this.checked);
    this.onChange(this.checked);
  }
}